import React, { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Text } from "@react-three/drei";

export function ParticleText({ setEntered, setTargetPosition, setLookAtTarget}) {
  const pointsRef = useRef();
  const { mouse, camera } = useThree();

  const lines = window.innerWidth <= 1024
  ? [
      "You won't believe",
      "what we can shape",
      "from the discarded"
    ]
  : [
      "You won't believe",
      "what we can shape from the discarded",
    ];
  const font = "700 100px Montserrat, sans-serif";
  const gap = 5;
  const lineHeight = 1;
  const size = 0.025;
  const color = "#ffffff";
  const depth = 0;

  const { geometry, basePositions } = useMemo(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const padding = 100;
    const maxWidth = 1800;
    canvas.width = maxWidth + padding * 2;
    canvas.height = 1000;

    ctx.font = font;
    ctx.textBaseline = "top";
    ctx.fillStyle = "white";

    const metrics = lines.map(line => ({ line, width: ctx.measureText(line).width }));
    const fontSizeMatch = / (\d+)px /.exec(` ${font} `);
    const fontPx = fontSizeMatch ? parseFloat(fontSizeMatch[1]) : 110;

    const actualLineHeight = fontPx * lineHeight;
    const totalHeight = metrics.length * actualLineHeight;

    canvas.height = Math.ceil(totalHeight + padding * 2);

    ctx.font = font;
    ctx.textBaseline = "top";
    ctx.fillStyle = "white";

    metrics.forEach((m, i) => {
      const x = Math.round((canvas.width - m.width) / 2);
      const y = Math.round(padding + i * actualLineHeight);
      ctx.fillText(m.line, x, y);
    });

    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const positions = [];

    for (let y = 0; y < img.height; y += gap) {
      for (let x = 0; x < img.width; x += gap) {
        const idx = (y * img.width + x) * 4;
        const alpha = img.data[idx + 3];
        if (alpha > 32 && img.data[idx] > 16) {
          // Inverser Y pour Three.js
          positions.push(x, img.height - y, 0);
        }
      }
    }

    const positionAttr = new Float32Array(positions);
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positionAttr, 3));

    geom.computeBoundingBox();
    const bb = geom.boundingBox;

    const targetWorldWidth = window.innerWidth <= 1024
  ? [
      5
    ]
  : [
      10
    ];
    const pixelWidth = bb.max.x - bb.min.x || 1;
    const scale = targetWorldWidth / pixelWidth;

    const cx = (bb.min.x + bb.max.x) / 2;
    const cy = (bb.min.y + bb.max.y) / 2.5;

    const basePositions = [];
    const arr = geom.attributes.position.array;
    for (let i = 0; i < arr.length; i += 3) {
      const x = (arr[i] - cx) * scale;
      const y = (arr[i + 1] - cy) * scale;
      const z = depth;

      basePositions.push(new THREE.Vector3(x, y, z));

      arr[i] = x;
      arr[i + 1] = y;
      arr[i + 2] = z;
    }

    geom.attributes.position.needsUpdate = true;
    return { geometry: geom, basePositions };
  }, [lines, font, gap, lineHeight, depth]);

  useFrame(() => {
    if (!pointsRef.current) return;
    if (mouse.x === 0 && mouse.y === 0) return;

    const arr = geometry.attributes.position.array;
    const vec = new THREE.Vector3(mouse.x, -mouse.y, 0.5).unproject(camera);
    const dir = vec.sub(camera.position).normalize();
    const distance = (depth - camera.position.z) / dir.z;
    const mousePos = camera.position.clone().add(dir.multiplyScalar(distance));

    for (let i = 0; i < basePositions.length; i++) {
      const base = basePositions[i];
      const dx = arr[i * 3] + mousePos.x;
      const dy = arr[i * 3 + 1] + mousePos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 1) {
        const force = 0.05 * (1 - dist);
        arr[i * 3] += dx * force;
        arr[i * 3 + 1] += dy * force;
      } else {
        arr[i * 3] = THREE.MathUtils.lerp(arr[i * 3], base.x, 0.05);
        arr[i * 3 + 1] = THREE.MathUtils.lerp(arr[i * 3 + 1], base.y, 0.05);
      }
    }
    geometry.attributes.position.needsUpdate = true;
  });

  function handleClick() {
    setTargetPosition(new THREE.Vector3(0, 0.7, 3));
    setLookAtTarget(new THREE.Vector3(0, 0.8, 4));
    setEntered(true);
  }

  return (
    <>
      <points ref={pointsRef} geometry={geometry} rotation={[0, 0, 0]} frustumCulled>
        <pointsMaterial size={size} sizeAttenuation depthWrite={false} color={color} />
      </points>
      <Text
        rotation={[0, 0, 0]}
        position={[0, -1.5, 1]}
        fontSize={0.2}
        onClick={handleClick}
        onPointerOver={() => { document.body.style.cursor = 'pointer' }}
        onPointerOut={() => { document.body.style.cursor = 'auto' }}
      >
        #createwithus
      </Text>
    </>
  );
}
